import { MouseEvent, useEffect, useState } from "react"
import { ReactComponent as PredIcon } from '../../../assets/Icons/adornments/SinglePredict.svg';

import PopupHeader from "../../../components/Popups/PopupHeader"
import PopupSideNav from "../../../components/Popups/PopupSideNav"
import PopupFooter from "../../../components/Popups/PopupFooter"
import Buttons, { Btn } from "../../../components/Buttons/Buttons";
import SelectCompound from "./RunPredictionComponents/SelectCompound";
import SuccessMessageModal from "../../../components/Popups/SuccessMessageModal";
import { ACSinglePredictDAGRequestModel, ModelFamily } from "../../../api/models";
import SetSinglePredictionParameters from "./RunPredictionComponents/SetSinglePredictionParameters";
import { classificationMethods, regressionMethods } from "../../../utils/supplementalData";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";
import { SelectListOption } from "../../../components/Filtering/PaginatedSelectList";
import SelectModelsV2 from "./RunPredictionComponents/SelectModelsV2";
import { customInstance } from "../../../api/custom-instance";


const navItems = ['Select Compound', 'Select Models', 'Set Prediction Parameters'];

const defaultForm: ACSinglePredictDAGRequestModel = {
    name: '',
    smiles: '',
    model_family_id_list: {},
    prediction_type: 'normal',
    tag_behavior: 'define',
    tags: [],
    molecule_highlighting: true,
    explainability_enabled: true,
}

export default function SingleCompoundPredictionModal({
    setPopupMessage,
    selectedCompound,
    selectedModelFamilyIds,
    handleCloseMenu,
    cancelFn,
    location,
}: {
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
    selectedCompound?: string,
    selectedModelFamilyIds?: number[],
    handleCloseMenu?: () => void,
    cancelFn?: () => void,
    location: 'modal' | 'dropdown' | 'header' | 'icon',
}) {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);


    const [ btns, setBtns ] = useState<Btn[]>([]);
    const [ selectedNavIndex, setSelectedNavIndex ] = useState(0);
    
    const [ form, setForm ] = useState<ACSinglePredictDAGRequestModel>(defaultForm);
    const [ smiles, setSmiles ] = useState('');


    const [ selectedModels, setSelectedModels ] = useState<ModelFamily[]>([]);

    const [ modelTypes, setModelTypes ] = useState<string[]>(classificationMethods.concat(regressionMethods));
    const [ isPrefetching, setIsPrefetching ] = useState(false);
    // const [ selectedIndex, setSelectedIndex ] = useState(0);

    const [ isSuccess, setIsSuccess ] = useState(false);
    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);

    const { handleSinglePrediction } = usePost({
        setPopupMessage,
        setIsDisabled,
        setIsLoading,
        setIsSuccess,
    });


    const handleClose = () => {
        setOpen(false);
        setShowSelect(false);
        resetValue();
        cancelFn && cancelFn();
        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    }

    const resetValue = () => {
        setSelectedNavIndex(0);
        setForm(defaultForm);
        setSmiles('');
        setSelectedModels([]);
        // setChosenModels([]);
        setIsDisabled(false);
        setIsLoading(false);
        setIsSuccess(false);
    }

    useEffect(() => {
        const fetchModels = async (ids: number[]) => {
            setIsPrefetching(true);
            try {
                const models: ModelFamily[] = [];
                
                const promises = ids.map(async (mfId) => {
                    const response = await customInstance({
                        url: `model_family/?model_family_id=${mfId}&include_model_family_metadata=true&include_model_stats=true&include_airflow_metadata=false`,
                        method: 'GET',
                    });
                    const data = response as unknown as ModelFamily;
                    models.push(data);
                });

                await Promise.all(promises);

                setSelectedModels(models);
            } catch (err:any) {
                console.log(err);
            }
            setIsPrefetching(false);
        }

        if (selectedCompound) {
            setSmiles(selectedCompound);
            setSelectedNavIndex(1);
        } else if (selectedModelFamilyIds !== undefined) {
            setSelectedNavIndex(0);
            fetchModels(selectedModelFamilyIds);
        }
    }, [
        selectedCompound,
        selectedModelFamilyIds,
    ]);

    useEffect(() => {
        if (!smiles || selectedModels.length === 0 || !form.name) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [
        smiles,
        selectedModels,
        form.name,
    ]);

    useEffect(() => {
        if (selectedModels.length > 0) {
            const tags: string[] = [];

            selectedModels.forEach(model => {
                const modelTags = model.model_family_metadata!.tags as string[] ?? [];
                modelTags.forEach(tag => {
                    const tagValue = tag ?? 'null';
                    if (!tags.includes(tagValue)) {
                        tags.push(tagValue);
                    }
                })
            });

            setForm((prevForm) => ({
                ...prevForm,
                tags,
            }))
        }
    }, [selectedModels]);

    useEffect(() => {
        const chosenModels: SelectListOption[] = selectedModels.length > 0 ? selectedModels.map(model => ({ id: model.id!, name: model.name ?? '' })) : [];

        setBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-single-prediction',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => handleClose(),
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'run-single-prediction-btn',
                label: "Run",
                action: (e: MouseEvent<HTMLButtonElement>) => { handleSinglePrediction(e, form, chosenModels, smiles, modelTypes) },
                isLoading,
            }
        ])

        //eslint-disable-next-line
    }, [isDisabled, isLoading, form, smiles, modelTypes, selectedModels]);

    const checkIfLocked = (index: number) => {
        if (navItems[index] === 'Set Prediction Parameters') {
            if (!smiles || selectedModels.length === 0) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    const checkIfComplete = (index: number) => {
        if (smiles && navItems[index] === 'Select Compound') {
            return true;
        }

        if (selectedModels.length > 0 && navItems[index] === 'Select Models') {
            return true;
        }

        if (smiles && selectedModels.length > 0 && navItems[index] === 'Set Prediction Parameters') {
            if (form.name!) {
                return true;
            }
        }

        return false;
    };

    



    return (
        <div>
            {location === 'modal' ? (
                <Buttons 
                    id='run-single-pred-btn' 
                    action={handleOpen} 
                    type="secondary" 
                    label="Run Prediction" 
                    styling='solid'
                    startingAdornment={<PredIcon />}
                />
            ) : location === 'icon' ? (
                <div className="h-full flex justify-center items-center">
                    <button onClick={handleOpen} className="w-[24px] text-disable hover:text-primary transition-all">
                        <PredIcon/>
                    </button>
                </div>
            ) : (
                <button onClick={handleOpen} className={`w-full px-5 py-3 text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                    <div className='flex justify-start items-center gap-3'>
                        <p className='text-nowrap'>{location === 'header' ? 'Single Compound Prediction' : 'Run Single Prediction'}</p>
                    </div>
                </button>
            )}
            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="single-prediction-popup"
            >
            {!isSuccess ? (
                <div className={`relative z-[1000] bg-white rounded-lg w-full max-w-[900px] popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Run Single Prediction"
                            hasOutline={true}
                            closeFn={handleClose}
                        />   
                        <div className="w-full flex justify-center items-stretch overflow-y-auto">
                            <PopupSideNav 
                                items={navItems.map((item, i) => ({
                                    label: item,
                                    type: 'number',
                                    isDisabled: checkIfLocked(i),
                                    isActive: selectedNavIndex === i ? true : false,
                                    isComplete: checkIfComplete(i),
                                    action: (e: MouseEvent<HTMLButtonElement>) => {setSelectedNavIndex(i)},
                                }))}
                                selectedIndex={selectedNavIndex}
                            />
                            <div className="w-full grow overflow-y-auto border-l border-outline">
                                {navItems.map((navItem, index) => (
                                    <div className={`w-full p-6 ${index === selectedNavIndex ? 'flex' : 'hidden'} flex-col gap-8 justify-start items-stretch min-h-[600px]`}>
                                        {navItem === 'Select Compound' ? (
                                            <SelectCompound
                                                smiles={smiles}
                                                setSmiles={setSmiles}
                                            />
                                        ) : navItem === 'Select Models' ? (
                                            <SelectModelsV2 
                                                preselectedModelFamilyIds={selectedModelFamilyIds}
                                                selectedModels={selectedModels}
                                                setSelectedModels={setSelectedModels}
                                                isPrefetching={isPrefetching}
                                                // selectedIndex={selectedIndex}
                                                // setSelectedIndex={setSelectedIndex}
                                            />
                                        ) : (
                                             <SetSinglePredictionParameters 
                                                form={form}
                                                setForm={setForm}
                                                modelTypes={modelTypes}
                                                setModelTypes={setModelTypes}
                                                showSelect={showSelect}
                                                numSelectedModels={selectedModels.length}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <PopupFooter 
                            hasOutline={true}
                            buttons={btns}
                        />
                    </div>
                </div>
            ) : (
                <SuccessMessageModal 
                    handleClose={handleClose}
                    message="Your prediction is being generated. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in the Resultsets page."
                />
            )}
            </ModalBase>
        </div>
    )
}